import {
  QueryClient,
  useQuery,
  useQueryClient,
  useMutation,
  UseMutationOptions,
} from '@tanstack/react-query';
import {
  BulkMCellEmployeeLogin,
  BulkMCellEmployeeLogout,
  BulkMCellLoginResult,
  JobFunction,
  JobFunctionInfo,
  JobFunctionTagDto,
  JobFunctionTagInfo,
  MCellEmployeeInMCellFilter,
  MCellEmployeeLoggedInFilter,
  MCellEmployeeLogin,
  MCellEmployeeLogout,
  MCellLoginDto,
} from './types';
import {
  mCellLogin,
  mCellLogout,
  getMCellEmployeesInMCell,
  getMCellEmployeesLoggedIn,
  getMCellEmployeeSelection,
  getMCellEmployeesList,
  getEmployeesList,
  createMCellEmployee,
  updateMCellEmployee,
  deleteMCellEmployee,
  bulkMCellLogin,
  bulkMCellLogout,
  getJobFunctions,
  createJobFunction,
  updateJobFunction,
  deleteJobFunction,
  getJobFunctionTags,
  createJobFunctionTag,
  updateJobFunctionTag,
  deleteJobFunctionTag,
  getMCellLoginLastUpdatedDate,
  checkEmployeeRefreshStatus,
  triggerManualEmployeeRefresh,
  downloadEmployeeReportCsv,
} from './api';
import { QueryKeys } from '../../api/queryKeys';
import { REFERENCE_DATA_STALE_TIME } from '../../queryClient';
import { useInvalidatingMutation } from '../../api/useInvalidatingMutation';
import {
  MCellEmployeeInput,
  MCellEmployeeListDto,
} from '../resourceTracking/types';
import { saveBlobToFile } from '../../utils/file';

/**
 * `useMCellEmployeesInMCellQuery` is a custom hook that uses the `useQuery` hook from `react-query` to fetch MCell Employees in a MCell.
 * It sets up a query function with `getMCellEmployeesInMCell` and the provided filter.
 * @remarks Retrieves MCell Employees with additional information on status in an MCell.
 *
 * @param filter - The filter object to be used in the query.
 * @returns The result object from `useQuery` which includes the data and status of the query.
 */
export const useMCellEmployeesInMCellQuery = (
  filter: MCellEmployeeInMCellFilter,
) => {
  return useQuery({
    queryKey: [QueryKeys.MCELL_EMPLOYEES_IN_MCELL, filter],
    queryFn: () => getMCellEmployeesInMCell(filter),
  });
};

/**
 * `useMCellEmployeesLoggedInQuery` is a custom hook that uses the `useQuery` hook from `react-query` to fetch MCell Employees that are logged in.
 * It sets up a query function with `getMCellEmployeesLoggedIn` and the provided filter.
 *
 * @param filter - The filter object to be used in the query.
 * @returns The result object from `useQuery` which includes the data and status of the query.
 */
export const useMCellEmployeesLoggedInQuery = (
  filter: MCellEmployeeLoggedInFilter,
) => {
  return useQuery({
    queryKey: [QueryKeys.MCELL_EMPLOYEES_LOGGED_IN, filter],
    queryFn: () => getMCellEmployeesLoggedIn(filter),
  });
};

/**
 * `useMCellEmployeeSelectionQuery` is a custom hook that uses the `useQuery` hook from `react-query`
 * to fetch MCell employees for a given inventory center.
 * The query is enabled only if `inventoryCenterId` is provided.
 *
 * @param inventoryCenterId - The current Inventory Center ID. Always required.
 * @param includeAllCenters - When true, fetches employees from all Inventory Centers with center
 *   metadata populated for client-side grouping. The query key includes this flag so the
 *   all-centers result is cached separately from the center-filtered result.
 * @param enabled - A boolean indicating whether the query should be enabled. Defaults to `true` if `inventoryCenterId` is provided.
 * @returns The result object from `useQuery` which includes the data, status, and other query properties.
 */
export const useMCellEmployeeSelectionQuery = (
  inventoryCenterId: string,
  includeAllCenters = false,
  enabled: boolean = !!inventoryCenterId,
) => {
  return useQuery({
    queryKey: [
      QueryKeys.MCELL_EMPLOYEE_SELECTION,
      inventoryCenterId,
      includeAllCenters,
    ],
    queryFn: () =>
      getMCellEmployeeSelection(inventoryCenterId, includeAllCenters),
    enabled,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    meta: { errorMessageKey: 'errorFetchingEmployees' },
  });
};

/**
 * `useMCellEmployeeLogin` is a custom hook that uses the `useMutation` hook from `react-query` to perform the login operation.
 * It sets up a mutation function with `mCellLogin` and an `onSuccess` callback.
 * The `onSuccess` callback updates the query data for 'mCellLogin' with the returned data from the mutation.
 *
 * @returns The result object from `useMutation` which includes data and methods to interact with the mutation.
 */
export const useMCellEmployeeLogin = (
  options?: UseMutationOptions<MCellLoginDto, Error, MCellEmployeeLogin>,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: mCellLogin,
    onSuccess: () => {
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: [QueryKeys.MCELL_EMPLOYEES_LOGGED_IN],
        }),
        queryClient.invalidateQueries({
          queryKey: [QueryKeys.MCELL_EMPLOYEES_IN_MCELL],
        }),
      ]).catch((error: Error) => {
        console.error('Login invalidation failed:', error);
      });
    },
    ...options,
  });
};

/**
 * `useMCellEmployeeLogout` is a custom hook that uses the `useMutation` hook from `react-query` to perform the logout operation.
 * It sets up a mutation function with `mCellLogout` and an `onSuccess` callback.
 * The `onSuccess` callback updates the query data for 'mCellLogout' with the returned data from the mutation.
 *
 * @returns The result object from `useMutation` which includes data and methods to interact with the mutation.
 */
export const useMCellEmployeeLogout = (
  options?: UseMutationOptions<MCellLoginDto, Error, MCellEmployeeLogout>,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: mCellLogout,
    onSuccess: () => {
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: [QueryKeys.MCELL_EMPLOYEES_LOGGED_IN],
        }),
        queryClient.invalidateQueries({
          queryKey: [QueryKeys.MCELL_EMPLOYEES_IN_MCELL],
        }),
      ]).catch((error) => {
        console.error('Logout invalidation failed:', error);
      });
    },
    ...options,
  });
};

/**
 * Custom hook for performing a bulk login operation for MCell Employees.
 * Uses the `useMutation` hook from `react-query` to execute the mutation.
 * On success, it invalidates the queries for logged-in employees and employees in MCell.
 *
 * @param {UseMutationOptions<BulkMCellLoginResult, Error, BulkMCellEmployeeLogin>} [options] - Optional mutation options.
 * @returns The result object from `useMutation` which includes data and methods to interact with the mutation.
 */
export const useBulkMCellEmployeeLogin = (
  options?: UseMutationOptions<
    BulkMCellLoginResult,
    Error,
    BulkMCellEmployeeLogin
  >,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: bulkMCellLogin,
    onSuccess: () => {
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: [QueryKeys.MCELL_EMPLOYEES_LOGGED_IN],
        }),
        queryClient.invalidateQueries({
          queryKey: [QueryKeys.MCELL_EMPLOYEES_IN_MCELL],
        }),
      ]).catch((error: Error) => {
        console.error('BulkMCellEmployeeLogin invalidation failed:', error);
      });
    },
    ...options,
  });
};

/**
 * Custom hook for performing a bulk logout operation for MCell Employees.
 * Uses the `useMutation` hook from `react-query` to execute the mutation.
 * On success, it invalidates the queries for logged-in employees and employees in MCell.
 *
 * @param {UseMutationOptions<BulkMCellLoginResult, Error, BulkMCellEmployeeLogout>} [options] - Optional mutation options.
 * @returns The result object from `useMutation` which includes data and methods to interact with the mutation.
 */
export const useBulkMCellEmployeeLogout = (
  options?: UseMutationOptions<
    BulkMCellLoginResult,
    Error,
    BulkMCellEmployeeLogout
  >,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: bulkMCellLogout,
    onSuccess: () => {
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: [QueryKeys.MCELL_EMPLOYEES_LOGGED_IN],
        }),
        queryClient.invalidateQueries({
          queryKey: [QueryKeys.MCELL_EMPLOYEES_IN_MCELL],
        }),
      ]).catch((error: Error) => {
        console.error('BulkMCellEmployeeLogout invalidation failed:', error);
      });
    },
    ...options,
  });
};

/**
 * `useMCellEmployeesListQuery` is a custom hook that fetches all active MCell Employees as DTOs.
 *
 * @returns The result object from `useQuery` which includes the data and status of the query.
 */
export const useMCellEmployeesListQuery = () => {
  return useQuery({
    queryKey: [QueryKeys.MCELL_EMPLOYEES_LIST],
    queryFn: () => getMCellEmployeesList(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

/**
 * `useEmployeesListQuery` is a custom hook that fetches all active Employees as DTOs.
 *
 * @returns The result object from `useQuery` which includes the data and status of the query.
 */
export const useEmployeesListQuery = () => {
  return useQuery({
    queryKey: [QueryKeys.EMPLOYEES_LIST],
    queryFn: () => getEmployeesList(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

/**
 * `useCreateMCellEmployeeMutation` is a custom hook for creating a new MCellEmployee.
 * Invalidates MCELL_EMPLOYEES_LIST query on success.
 */
export const useCreateMCellEmployeeMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (mCellEmployeeInput: MCellEmployeeInput) =>
      createMCellEmployee(mCellEmployeeInput),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.MCELL_EMPLOYEES_LIST],
      });
    },
    onError: (error: any) => {
      console.error('Failed to create Work Cell Employee:', error);
    },
  });
};

/**
 * `useUpdateMCellEmployeeMutation` is a custom hook for updating an existing MCellEmployee.
 * Invalidates MCELL_EMPLOYEES_LIST query on success.
 */
export const useUpdateMCellEmployeeMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      mCellEmployeeInput,
    }: {
      id: string;
      mCellEmployeeInput: MCellEmployeeInput;
    }) => updateMCellEmployee(id, mCellEmployeeInput),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.MCELL_EMPLOYEES_LIST],
      });
    },
    onError: (error: any) => {
      console.error('Failed to update Work Cell Employee:', error);
    },
  });
};

/**
 * `useDeleteMCellEmployeeMutation` is a custom hook for deleting (soft delete) an MCellEmployee.
 * Uses optimistic update to immediately remove from UI, with rollback on error.
 * Invalidates MCELL_EMPLOYEES_LIST query.
 */
export const useDeleteMCellEmployeeMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteMCellEmployee(id),
    onMutate: async (id: string) => {
      await queryClient.cancelQueries({
        queryKey: [QueryKeys.MCELL_EMPLOYEES_LIST],
      });

      const previousEmployeesList = queryClient.getQueryData<
        MCellEmployeeListDto[]
      >([QueryKeys.MCELL_EMPLOYEES_LIST]);

      queryClient.setQueryData<MCellEmployeeListDto[]>(
        [QueryKeys.MCELL_EMPLOYEES_LIST],
        (old) => old?.filter((employee) => employee.id !== id) ?? [],
      );

      return { previousEmployeesList };
    },
    onError: (error: any, _id, context) => {
      console.error('Failed to delete Work Cell Employee:', error);
      if (context?.previousEmployeesList) {
        queryClient.setQueryData(
          [QueryKeys.MCELL_EMPLOYEES_LIST],
          context.previousEmployeesList,
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.MCELL_EMPLOYEES_LIST],
      });
    },
  });
};

/**
 * Fetches the Job Functions of an Inventory Center.
 * @param inventoryCenterId
 */
export const useJobFunctionsQuery = (inventoryCenterId: string) =>
  useQuery({
    queryKey: [QueryKeys.JOB_FUNCTIONS, inventoryCenterId],
    queryFn: () => getJobFunctions(inventoryCenterId),
    meta: { errorMessageKey: 'errorFetchingJobFunctions' },
  });

/**
 * Fetches all Job Function Tags.
 */
export const useJobFunctionTagsQuery = () =>
  useQuery({
    queryKey: [QueryKeys.JOB_FUNCTION_TAGS],
    queryFn: getJobFunctionTags,
    staleTime: REFERENCE_DATA_STALE_TIME,
    meta: { errorMessageKey: 'errorFetchingJobFunctionTags' },
  });

/**
 * Replaces the item with the same id, or appends it when `belongs` says the list should hold it.
 */
const upsertById = <T extends { id: string }>(
  list: T[],
  item: T,
  belongs = true,
) =>
  list.some((x) => x.id === item.id)
    ? list.map((x) => (x.id === item.id ? item : x))
    : belongs
      ? [...list, item]
      : list;

/**
 * Upserts a Job Function into the cached lists, appending only to lists of its Inventory Center.
 */
const upsertJobFunction = (list: JobFunction[], info: JobFunctionInfo) =>
  upsertById(
    list,
    info as JobFunction,
    list.some((x) => x.inventoryCenterId === info.inventoryCenterId),
  );

const upsertJobFunctionTag = (
  list: JobFunctionTagDto[],
  info: JobFunctionTagInfo,
) => upsertById(list, info as JobFunctionTagDto);

const removeById = <T extends { id: string }>(list: T[], id: string) =>
  list.filter((x) => x.id !== id);

/**
 * Creates a Job Function, optimistically adding it to its Inventory Center's list.
 */
export const useCreateJobFunctionMutation = () =>
  useInvalidatingMutation(
    [QueryKeys.JOB_FUNCTIONS],
    createJobFunction,
    'errorCreatingJobFunction',
    upsertJobFunction,
  );

/**
 * Updates a Job Function, optimistically replacing it in the cached lists.
 */
export const useUpdateJobFunctionMutation = () =>
  useInvalidatingMutation(
    [QueryKeys.JOB_FUNCTIONS],
    updateJobFunction,
    'errorUpdatingJobFunction',
    upsertJobFunction,
  );

/**
 * Deletes a Job Function, optimistically removing it from the cached lists.
 */
export const useDeleteJobFunctionMutation = () =>
  useInvalidatingMutation(
    [QueryKeys.JOB_FUNCTIONS],
    deleteJobFunction,
    'errorDeletingJobFunction',
    removeById<JobFunction>,
  );

/**
 * Creates a Job Function Tag, optimistically adding it to the list.
 */
export const useCreateJobFunctionTagMutation = () =>
  useInvalidatingMutation(
    [QueryKeys.JOB_FUNCTION_TAGS],
    createJobFunctionTag,
    'errorCreatingJobFunctionTag',
    upsertJobFunctionTag,
  );

/**
 * Updates a Job Function Tag, optimistically replacing it in the list.
 */
export const useUpdateJobFunctionTagMutation = () =>
  useInvalidatingMutation(
    [QueryKeys.JOB_FUNCTION_TAGS],
    updateJobFunctionTag,
    'errorUpdatingJobFunctionTag',
    upsertJobFunctionTag,
  );

/**
 * Deletes a Job Function Tag, optimistically removing it from the list.
 */
export const useDeleteJobFunctionTagMutation = () =>
  useInvalidatingMutation(
    [QueryKeys.JOB_FUNCTION_TAGS],
    deleteJobFunctionTag,
    'errorDeletingJobFunctionTag',
    removeById<JobFunctionTagDto>,
  );

/**
 * Fetches when MCell Logins were last imported.
 */
export const useMCellLoginLastUpdatedDateQuery = () =>
  useQuery({
    queryKey: [QueryKeys.MCELL_LOGIN_LAST_UPDATED_DATE],
    queryFn: getMCellLoginLastUpdatedDate,
    meta: { errorMessageKey: 'errorFetchingLoginLastUpdatedDate' },
  });

/**
 * Whether an employee refresh is running. The SignalR `Employee` push writes this cache entry directly.
 */
export const useEmployeeRefreshRunningQuery = () =>
  useQuery({
    queryKey: [QueryKeys.EMPLOYEE_REFRESH_STATUS],
    queryFn: checkEmployeeRefreshStatus,
    select: (status) => status.isLeased,
    meta: { errorMessageKey: 'errorCheckingEmployeeRefreshStatus' },
  });

/**
 * Caches the employee refresh running flag pushed over SignalR.
 */
export const setEmployeeRefreshRunning = (
  queryClient: QueryClient,
  running: boolean,
) =>
  queryClient.setQueryData([QueryKeys.EMPLOYEE_REFRESH_STATUS], {
    isLeased: running,
  });

/**
 * Starts a manual refresh of AD Users and MCell Employees.
 */
export const useTriggerManualEmployeeRefreshMutation = () =>
  useMutation({
    mutationFn: triggerManualEmployeeRefresh,
    meta: { errorMessageKey: 'errorTriggeringManualEmployeeRefresh' },
  });

/**
 * Downloads the Employee Search report as a CSV file with the given filename.
 */
export const useDownloadEmployeeReportCsvMutation = () =>
  useMutation({
    mutationFn: async (filename: string) =>
      saveBlobToFile(await downloadEmployeeReportCsv(), filename),
    meta: { errorMessageKey: 'errorExportingEmployeeSearchReport' },
  });
